import React, { useEffect, useState } from 'react';
import { marked } from 'marked';
import Seo from '../components/Seo';
import { Shell, articleClass, DemoCta } from '../components/Article';
import { ORIGIN, faqFrom, faqPage, useJsonLd } from '../lib/jsonld';

// One entry per markdown page. The .md lives in public/ at `${path}.md`,
// so prerender + sitemap discover pages from the files themselves.
export const PAGES = [
  {
    path: '/compare/emboscale-vs-traditional-olp',
    label: 'Emboscale vs traditional OLP',
    title: 'Emboscale vs traditional offline programming (OLP) — Emboscale',
    description:
      'An honest comparison of the Factory Agent with traditional robot offline programming tools like RoboDK, Octopuz, and Robotmaster — and when each is the better choice.',
  },
  {
    path: '/compare/emboscale-vs-enterprise-suites',
    label: 'Emboscale vs enterprise suites',
    title: 'Emboscale vs enterprise simulation suites — Emboscale',
    description:
      'How the Factory Agent compares to enterprise factory-simulation suites like Siemens Tecnomatix and Dassault Delmia — and when each is the better choice.',
  },
  {
    path: '/capabilities/factory-layout-planning',
    label: 'Factory layout planning',
    title: 'Factory layout software: AI layout planning in 3D — Emboscale',
    description:
      'Factory layout software compared: AutoCAD, Factory Design Utilities, Visual Components, Plant Simulation, FlexSim and AI. Layout types, free options and FAQ.',
  },
  {
    path: '/capabilities/line-balancing',
    label: 'Line balancing',
    title: 'Line balancing software — takt time, cycle time, bottlenecks — Emboscale',
    description:
      'What line balancing means in manufacturing, how takt time and cycle time relate, and how the Factory Agent balances a line and re-balances it from a prompt.',
  },
  {
    path: '/capabilities/shift-scheduling',
    label: 'Shift scheduling',
    title: 'Production shift scheduling with CP-SAT — Emboscale',
    description:
      'Why plant schedules built in spreadsheets break, what constraint solving changes, and how the Factory Agent produces auditable shift schedules with due-date risk flagging.',
  },
  {
    path: '/capabilities/robot-program-verification',
    label: 'Robot program verification',
    title: 'Robot program verification — the 8-check kinematic oracle — Emboscale',
    description:
      'Why AI-written robot programs must be machine-verified, and what the 8-check kinematic verification suite proves before any program ships in a deploy pack.',
  },
];

// WebPage (+ FAQPage when the page has a "Frequently asked questions" section).
const pageJsonLd = (page, md) => {
  const url = `${ORIGIN}${page.path}/`;
  const graph = [{
    '@type': 'WebPage',
    name: page.title,
    description: page.description,
    url,
    publisher: { '@type': 'Organization', name: 'Emboscale', url: ORIGIN },
    about: { '@type': 'SoftwareApplication', name: 'Emboscale Factory Agent', url: ORIGIN },
  }];
  const faq = faqFrom(md);
  if (faq.length) graph.push(faqPage(faq));
  return { '@context': 'https://schema.org', '@graph': graph };
};

const ContentPage = ({ page }) => {
  const [html, setHtml] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);
  useJsonLd(jsonLd);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${page.path}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((md) => {
        const body = md.replace(/^---[\s\S]*?---\s*/, '');
        setHtml(marked.parse(body));
        setJsonLd(pageJsonLd(page, body));
      })
      .catch(() => setHtml('<p>Page not found.</p>'));
  }, [page.path]);

  return (
    <Shell>
      <Seo title={page.title} description={page.description} path={page.path} />
      <article className={articleClass} dangerouslySetInnerHTML={html ? { __html: html } : undefined}>
        {html ? undefined : <p className="text-gray-500 mt-8">Loading…</p>}
      </article>
      {html && <DemoCta />}
    </Shell>
  );
};

export default ContentPage;
